import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  lottieAnimations,
  COLOR_TOKENS,
  UnlockStatus,
  TrackAssessmentResult,
  AcademicTerm,
  AppIcon,
  TIERS,
  TRACK_CODE_TO_NAME,
  useContainerQuery,
  formatLongDate,
} from '@backrs_ui/common-ui';

import { ReactComponent as Flags404 } from '@backrs_ui/common-ui/src/assets/images/404-image.svg';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useGetTrackAssessmentHistoryQuery,
  useGetAcademicGradesQuery,
} from '@backrs_ui/grow-ui/src/redux/trackAssessment/trackAssessment.api';
import { selectProfilePhoto } from '@backrs_ui/grow-ui/src/redux/user/user';

import { Box, Button, Divider, Typography, SxProps, Theme } from '@mui/material';
import Lottie from 'lottie-react';

import { useAgentContext } from './CareerTrackTimelineChart.agentContext';
import { ChartLegend } from './ChartLegend';
import { ProfileStrengthModal } from './ProfileStrengthModal';
import { TimelineChartSvg, type AssessmentDataPoint } from './TimelineChartSvg';
import type { useAssessmentBreakdown } from '../hooks/useAssessmentBreakdown';
import { getStatusColor } from '../trackAssessment.constants';

export const CHART_MAX_WIDTH = '900px';

const getAcademicTerm = (date: string): AcademicTerm => {
  const month = new Date(date).getMonth();

  if (month >= 8 && month <= 10) return 'Fall';
  if (month >= 5 && month <= 7) return 'Summer';
  if (month >= 2 && month <= 4) return 'Spring';

  return 'Winter';
};

const TERM_ORDER: AcademicTerm[] = ['Fall', 'Winter', 'Spring', 'Summer'];

/**
 * Fills the timeline from the earliest real assessment through the user's
 * current grade+term. Where a real data point exists, it's used; gaps are
 * filled by carrying forward the most recent result.
 *
 * Grade and term are walked as one combined position. The history can order a
 * Summer assessment ahead of the Fall one in the same grade — the two use
 * different school-year boundaries — so neither the start nor the end can be
 * read off the ends of the list without dropping real points.
 */
const fillForwardDataPoints = (
  realPoints: AssessmentDataPoint[],
  gradeValues: string[],
  targetGradeValue: string,
  targetTerm: AcademicTerm,
): AssessmentDataPoint[] => {
  const positionOf = (gradeIndex: number, term: AcademicTerm) =>
    gradeIndex * TERM_ORDER.length + TERM_ORDER.indexOf(term);

  const placed = realPoints
    .map((point) => ({ point, gradeIndex: gradeValues.indexOf(point.gradeValue) }))
    .filter(({ gradeIndex }) => gradeIndex >= 0)
    .map(({ point, gradeIndex }) => ({ point, position: positionOf(gradeIndex, point.term) }));

  const targetGrade = gradeValues.indexOf(targetGradeValue);

  if (!placed.length || targetGrade < 0) return realPoints;

  const earliest = placed.reduce((first, entry) =>
    entry.position < first.position ? entry : first,
  );
  const endPosition = Math.max(
    positionOf(targetGrade, targetTerm),
    ...placed.map((entry) => entry.position),
  );

  const pointByPosition = new Map(placed.map(({ point, position }) => [position, point]));

  const filled: AssessmentDataPoint[] = [];
  let carriedResult = earliest.point.result;

  for (let position = earliest.position; position <= endPosition; position++) {
    const realPoint = pointByPosition.get(position);

    if (realPoint) {
      carriedResult = realPoint.result;
      filled.push(realPoint);
    } else {
      filled.push({
        gradeValue: gradeValues[Math.floor(position / TERM_ORDER.length)],
        term: TERM_ORDER[position % TERM_ORDER.length],
        result: carriedResult,
      });
    }
  }

  return filled;
};

const ExplainChartButton = ({ buttonSize = 'md' }: { buttonSize?: 'sm' | 'md' }) => (
  <Box onClick={(e) => e.stopPropagation()}>
    <LubavButton
      id="assess-track-explain-chart-btn"
      label="Explain this to me"
      prompt="Explain the Are You On Track chart to me. Walk me through each element — the black line, the colored circles (mention their actual colors), the blue shaded area, and the gray dotted lines. What does my current assessment show?"
      allowTools={false}
      buttonSize={buttonSize}
    />
  </Box>
);

const ASSESS_MY_TRACK_STRINGS = appStrings.modules.assessMyTrack;
const TIMELINE_CHART_STRINGS = ASSESS_MY_TRACK_STRINGS.timelineChart;

const LEGEND_STATUSES_DISPLAY_ORDER: readonly UnlockStatus[] = [
  'on_track',
  'developing',
  'not_yet',
] as const;

type CareerTrackTimelineChartProps = Readonly<{
  isLoading?: boolean;
  assessmentBreakdown: ReturnType<typeof useAssessmentBreakdown>;
  trackAssessmentResult?: TrackAssessmentResult;
  hideLegend?: boolean;
  onEditChecklist?: () => void;
  includeBlueprintText?: boolean;
  showLastAssessed?: boolean;
}>;

export const CareerTrackTimelineChart = ({
  isLoading,
  assessmentBreakdown,
  trackAssessmentResult,
  hideLegend = false,
  onEditChecklist,
  includeBlueprintText = false,
  showLastAssessed = false,
}: CareerTrackTimelineChartProps) => {
  const { minimumTrack } = assessmentBreakdown;

  const [containerRef, isCompact] = useContainerQuery(400);
  const [isProfileStrengthModalOpen, setIsProfileStrengthModalOpen] = useState(false);
  const { data: assessmentHistory, isError: isErrorHistory } =
    useGetTrackAssessmentHistoryQuery(undefined);
  const { data: academicGrades, isError: isErrorGrades } = useGetAcademicGradesQuery(undefined);
  const profileImageUrl = useAppSelector(selectProfilePhoto);

  const assessmentDataPoints = useMemo(() => {
    if (!assessmentHistory || !academicGrades) return [];

    const realPoints: AssessmentDataPoint[] = assessmentHistory.flatMap((result) => {
      const gradeValue = result.academicGrade?.value;

      if (!gradeValue) return [];

      // prefer the API term — it anchors incoming students at the start of their grade; date-only can't
      const term = result.academicTerm ?? getAcademicTerm(result.created_at);

      return [{ gradeValue, term, result }];
    });

    if (realPoints.length === 0) return [];

    const currentGradeValue = trackAssessmentResult?.academicGrade?.value;

    if (!currentGradeValue) return realPoints;

    const currentTerm =
      trackAssessmentResult?.academicTerm ?? getAcademicTerm(new Date().toISOString());

    return fillForwardDataPoints(
      realPoints,
      academicGrades.map((g) => g.value),
      currentGradeValue,
      currentTerm,
    );
  }, [assessmentHistory, academicGrades, trackAssessmentResult]);

  useAgentContext(trackAssessmentResult ?? null, minimumTrack, assessmentDataPoints);

  const descriptionContent = useMemo(() => {
    if (!trackAssessmentResult) return null;

    const careerTrack =
      trackAssessmentResult.careerTrack &&
      TRACK_CODE_TO_NAME[trackAssessmentResult.careerTrack.code]
        ? TRACK_CODE_TO_NAME[trackAssessmentResult.careerTrack.code]
        : TIERS.SEMISKILLED;

    const currentTrackText = TIMELINE_CHART_STRINGS.description(careerTrack.toLowerCase());

    if (!includeBlueprintText || !minimumTrack) return currentTrackText;

    return (
      <>
        {currentTrackText} {TIMELINE_CHART_STRINGS.blueprintDescription.prefix}
        <Link to={APP_ROUTE.MY_BLUEPRINT} style={blueprintLinkStyle}>
          {TIMELINE_CHART_STRINGS.blueprintDescription.linkText}
        </Link>
        {TIMELINE_CHART_STRINGS.blueprintDescription.suffix(minimumTrack.toLowerCase())}
      </>
    );
  }, [trackAssessmentResult, minimumTrack, includeBlueprintText]);

  if (!isLoading && (isErrorHistory || isErrorGrades)) {
    return (
      <Box sx={placeholderSx}>
        <Typography variant="display-sm-semibold" sx={placeholderTextSx}>
          {TIMELINE_CHART_STRINGS.errorLoadingChart}
        </Typography>
        <Box sx={placeholderImageContainerSx}>
          <Flags404 style={placeholderImageSx} />
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={containerSx}>
      {isLoading && (
        <Box sx={loadingOverlaySx}>
          <Lottie animationData={lottieAnimations.dataSync} style={lottieStyle} />
        </Box>
      )}
      {/* Title row: title + buttons */}
      <Box sx={titleRowSx}>
        <Box sx={titleAndDateSx}>
          <Typography sx={headerTitleSx}>{TIMELINE_CHART_STRINGS.header}</Typography>
          {showLastAssessed && trackAssessmentResult?.created_at && (
            <Typography sx={lastAssessedSx}>
              {TIMELINE_CHART_STRINGS.lastAssessed(
                formatLongDate(trackAssessmentResult.created_at),
              )}
            </Typography>
          )}
        </Box>
        {hideLegend && (
          <Box sx={{ marginLeft: 'auto' }}>
            <ExplainChartButton buttonSize={isCompact ? 'sm' : 'md'} />
          </Box>
        )}
        {!hideLegend && (
          <Box sx={titleButtonsSx}>
            <ExplainChartButton buttonSize={isCompact ? 'sm' : 'md'} />
            {onEditChecklist && (
              <Button
                hierarchy="Secondary gray"
                size="sm"
                icon="Leading"
                onClick={onEditChecklist}
                startIcon={
                  <AppIcon
                    iconName="RefreshCcw01"
                    diameter={isCompact ? '16px' : '20px'}
                    color="Gray/700"
                  />
                }
                sx={isCompact ? compactButtonSx : undefined}
                id="assess-track-edit-checklist-btn"
              >
                {ASSESS_MY_TRACK_STRINGS.updateAssessment}
              </Button>
            )}
          </Box>
        )}
      </Box>
      {/* Description + legends */}
      <Box sx={headerSx}>
        <Box sx={headerTitleContainerSx}>
          <Typography variant="text-sm-regular" sx={descriptionTextStyleSx}>
            {descriptionContent}
          </Typography>
        </Box>
        {!hideLegend && (
          <Box sx={legendsDesktopSx}>
            <ChartLegend
              title={TIMELINE_CHART_STRINGS.yourTrackLabel}
              items={[
                {
                  icon: <Box sx={targetTrackSwatchSx} />,
                  label: TIMELINE_CHART_STRINGS.yourTrackLegend.targetTracks,
                },
                {
                  icon: <Box sx={solidLineSwatchSx} />,
                  label: TIMELINE_CHART_STRINGS.yourTrackLegend.currentTrack,
                },
                {
                  icon: <Box sx={dashedLineSwatchSx} />,
                  label: TIMELINE_CHART_STRINGS.yourTrackLegend.yourPotential,
                },
              ]}
            />
            <ChartLegend
              title={TIMELINE_CHART_STRINGS.profileStrengthLabel}
              onInfoClick={() => setIsProfileStrengthModalOpen(true)}
              items={LEGEND_STATUSES_DISPLAY_ORDER.map((status) => ({
                icon: (
                  <AppIcon
                    diameter="10px"
                    iconName="Circle"
                    color="Colors/Base/Black"
                    fill={getStatusColor(status)}
                  />
                ),
                label: TIMELINE_CHART_STRINGS.unlockStatusLabels[status],
              }))}
            />
          </Box>
        )}
      </Box>

      {/* Chart */}
      <Divider sx={chartDividerSx} />
      <Box sx={chartWrapperSx}>
        <TimelineChartSvg
          academicGrades={academicGrades ?? []}
          assessmentDataPoints={assessmentDataPoints}
          minimumTrack={minimumTrack}
          profileImageUrl={profileImageUrl}
        />
      </Box>
      {/* Legends below chart on small containers */}
      {!hideLegend && (
        <Box sx={legendsMobileSx}>
          <Divider sx={legendsDividerSx} />
          <Box sx={legendsMobileRowSx}>
            <Box sx={{ flex: 1 }}>
              <ChartLegend
                title={TIMELINE_CHART_STRINGS.yourTrackLabel}
                items={[
                  {
                    icon: <Box sx={targetTrackSwatchSx} />,
                    label: TIMELINE_CHART_STRINGS.yourTrackLegend.targetTracks,
                  },
                  {
                    icon: <Box sx={solidLineSwatchSx} />,
                    label: TIMELINE_CHART_STRINGS.yourTrackLegend.currentTrack,
                  },
                  {
                    icon: <Box sx={dashedLineSwatchSx} />,
                    label: TIMELINE_CHART_STRINGS.yourTrackLegend.yourPotential,
                  },
                ]}
                bordered={false}
              />
            </Box>
            <Divider orientation="vertical" flexItem sx={legendsVerticalDividerSx} />
            <Box sx={{ flex: 1 }}>
              <ChartLegend
                title={TIMELINE_CHART_STRINGS.profileStrengthLabel}
                bordered={false}
                onInfoClick={() => setIsProfileStrengthModalOpen(true)}
                items={LEGEND_STATUSES_DISPLAY_ORDER.map((status) => ({
                  icon: (
                    <AppIcon
                      diameter="10px"
                      iconName="Circle"
                      color="Colors/Base/Black"
                      fill={getStatusColor(status)}
                    />
                  ),
                  label: TIMELINE_CHART_STRINGS.unlockStatusLabels[status],
                }))}
              />
            </Box>
          </Box>
        </Box>
      )}
      <ProfileStrengthModal
        open={isProfileStrengthModalOpen}
        onClose={() => setIsProfileStrengthModalOpen(false)}
      />
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  containerType: 'inline-size',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${COLOR_TOKENS['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
  pt: backrs.spacing['spacing-lg'],
  '@container (min-width: 400px)': {
    pt: backrs.spacing['spacing-2xl'],
  },
  '@container (min-width: 600px)': {
    pt: backrs.spacing['spacing-3xl'],
  },
  width: '100%',
  minWidth: 0,
  minHeight: 0,
  maxWidth: CHART_MAX_WIDTH,
  overflow: 'hidden',
});

const chartWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  pl: backrs.spacing['spacing-lg'],
  '@container (min-width: 400px)': {
    pl: backrs.spacing['spacing-2xl'],
  },
  '@container (min-width: 600px)': {
    pl: backrs.spacing['spacing-3xl'],
  },
});

const chartDividerSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-secondary'],
});

const loadingOverlaySx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: backrs.colors['Colors/Base/White'],
  borderRadius: backrs.radius['radius-xl'],
  zIndex: 1,
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: backrs.spacing['spacing-lg'],
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
  px: backrs.spacing['spacing-lg'],
  '@container (min-width: 400px)': {
    marginBottom: backrs.spacing['spacing-2xl'],
    gap: backrs.spacing['spacing-2xl'],
    px: backrs.spacing['spacing-2xl'],
  },
  '@container (min-width: 600px)': {
    px: backrs.spacing['spacing-3xl'],
  },
  '@container (min-width: 650px)': {
    flexDirection: 'row',
  },
});

const legendsDesktopSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'none',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-lg'],
  flexShrink: 0,
  '@container (min-width: 400px)': {
    display: 'flex',
  },
});

const legendsMobileSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  pb: backrs.spacing['spacing-lg'],
  '@container (min-width: 400px)': {
    display: 'none',
  },
});

const legendsDividerSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Base/Black'],
  my: backrs.spacing['spacing-lg'],
});

const legendsMobileRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-lg'],
  px: backrs.spacing['spacing-lg'],
  '@container (min-width: 400px)': {
    px: backrs.spacing['spacing-2xl'],
  },
});

const legendsVerticalDividerSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: COLOR_TOKENS['Colors/Base/Black'],
  my: `calc(-1 * ${backrs.spacing['spacing-lg']})`,
});

const targetTrackSwatchSx: SxProps<Theme> = {
  width: 14,
  height: 10,
  borderRadius: '4px',
  backgroundColor: COLOR_TOKENS['Blue/100'],
  opacity: 0.7,
};

const solidLineSwatchSx: SxProps<Theme> = {
  width: 16,
  height: 0,
  borderTop: `2px solid ${COLOR_TOKENS['Colors/Base/Black']}`,
};

const dashedLineSwatchSx: SxProps<Theme> = {
  width: 16,
  height: 0,
  borderTop: `2px dashed ${COLOR_TOKENS['Gray/400']}`,
};

const headerTitleSx: SxProps<Theme> = ({ backrs }) => ({
  typography: 'text-md-semibold',
  color: backrs.colors['Gray/900'],
  '@container (min-width: 400px)': {
    typography: 'text-lg-semibold',
  },
  '@container (min-width: 600px)': {
    typography: 'display-xs-semibold',
  },
});

const titleAndDateSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const lastAssessedSx: SxProps<Theme> = ({ backrs }) => ({
  typography: 'text-xs-regular',
  color: backrs.colors['Colors/Text/text-tertiary'],
  '@container (min-width: 600px)': {
    typography: 'text-sm-regular',
  },
});

const descriptionTextStyleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/900'],
  typography: 'text-xs-regular',
  '@container (min-width: 400px)': {
    typography: 'text-sm-regular',
  },
  '@container (min-width: 600px)': {
    typography: 'text-md-regular',
  },
});

const blueprintLinkStyle: React.CSSProperties = {
  color: '#3B82F6',
  fontWeight: 700,
  textDecoration: 'none',
  fontSize: 'inherit',
  lineHeight: 'inherit',
};

const headerTitleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  '@container (min-width: 400px)': {
    gap: backrs.spacing['spacing-xs'],
  },
  flex: 1,
  minWidth: 0,
});

const placeholderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  gap: backrs.spacing['spacing-2xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
  padding: backrs.spacing['spacing-2xl'],
  textAlign: 'center',
});

const placeholderTextSx: SxProps<Theme> = {
  maxWidth: '370px',
};

const placeholderImageContainerSx: SxProps<Theme> = {
  maxWidth: '100%',
  '@container (min-width: 600px)': {
    maxWidth: '370px',
  },
  width: '100%',
};

const placeholderImageSx: React.CSSProperties = {
  width: '100%',
  height: 'auto',
};

const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
  flexWrap: 'wrap',
  marginBottom: backrs.spacing['spacing-md'],
  px: backrs.spacing['spacing-lg'],
  '@container (min-width: 400px)': {
    px: backrs.spacing['spacing-2xl'],
  },
  '@container (min-width: 600px)': {
    px: backrs.spacing['spacing-3xl'],
  },
});

const titleButtonsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  marginLeft: 'auto',
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  '@container (max-width: 400px)': {
    marginLeft: 0,
    justifyContent: 'flex-start',
  },
  '& .MuiButton-root': {
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
});

const compactButtonSx: SxProps<Theme> = ({ backrs }) => ({
  py: backrs.spacing['spacing-xxs'],
  px: backrs.spacing['spacing-sm'],
  fontWeight: 400,
  minHeight: 0,
  borderWidth: '1px',
});

const lottieStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
};
